// EDEN · tiny path-based router with a role/auth guard.

import { me } from "./state";
import { appRoot } from "./dom";
import { isNetworkError, renderOffline } from "./offline";
import { renderSignin } from "./pages/signin";
import { renderHome } from "./pages/home";
import { renderClasses } from "./pages/classes";
import { renderClassView } from "./pages/class-view";
import { renderThisWeek } from "./pages/this-week";
import { renderPlanNew } from "./pages/plan-new";
import { renderPlanView } from "./pages/plan-view";
import { renderLeadStatus } from "./pages/lead-status";
import { renderCalendar } from "./pages/calendar";
import { renderCalendarImport } from "./pages/calendar-import";
import { renderDashboard } from "./pages/dashboard";
import { renderAdmin } from "./pages/admin";
import { renderInsights } from "./pages/insights";

type Params = Record<string, string>;
type Render = (app: HTMLElement, params: Params) => void | Promise<void>;

interface Route {
  pattern: RegExp;
  keys: string[];
  render: Render;
  pub?: boolean;          // reachable when signed out
  role?: "lead";          // restricted to a role
}

function route(path: string, render: Render, opts: { pub?: boolean; role?: "lead" } = {}): Route {
  const keys: string[] = [];
  const pattern = new RegExp(
    "^" +
      path.replace(/:[^/]+/g, (m) => {
        keys.push(m.slice(1));
        return "([^/]+)";
      }) +
      "/?$",
  );
  return { pattern, keys, render, ...opts };
}

const routes: Route[] = [
  route("/signin", renderSignin, { pub: true }),
  route("/", renderHome),
  route("/classes", renderClasses),
  route("/classes/:id", renderClassView),
  route("/this-week", renderThisWeek),
  route("/plan/new", renderPlanNew),
  route("/plan/:id", renderPlanView),
  route("/calendar", renderCalendar),
  route("/calendar/import", renderCalendarImport),
  route("/dashboard", renderDashboard),
  route("/status", renderLeadStatus, { role: "lead" }),
  route("/insights", renderInsights, { role: "lead" }),
  route("/admin", renderAdmin, { role: "lead" }),
];

export function navigate(path: string): void {
  history.pushState({}, "", path);
  void render();
}

async function render(): Promise<void> {
  const path = location.pathname;
  const user = me();

  // Auth guard: only /signin is public.
  for (const r of routes) {
    const m = r.pattern.exec(path);
    if (!m) continue;

    if (!r.pub && !user) return navigate("/signin");
    if (r.pub && user) return navigate("/");
    if (r.role && user?.auth_role !== r.role) return navigate("/");

    const params: Params = {};
    r.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1]!)));
    try {
      await r.render(appRoot(), params);
    } catch (err) {
      // Offline (a failed fetch): show the calm offline state instead of a blank page.
      // A real server error (ApiError) is left to bubble — that's not "offline".
      if (isNetworkError(err)) renderOffline(appRoot());
      else throw err;
    }
    return;
  }

  // Unknown path → home (or signin if signed out).
  navigate(user ? "/" : "/signin");
}

export function initRouter(): void {
  window.addEventListener("popstate", () => void render());
  // Self-heal: when the signal returns (or the offline panel's "Try again" fires), re-render
  // the current route so the data loads without a manual reload.
  window.addEventListener("online", () => void render());
  window.addEventListener("eden:rerender", () => void render());
  // Intercept in-app links marked with data-link.
  document.addEventListener("click", (e) => {
    const a = (e.target as HTMLElement).closest("a[data-link]") as HTMLAnchorElement | null;
    if (a && a.origin === location.origin) {
      e.preventDefault();
      navigate(a.pathname);
    }
  });
  void render();
}
